import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Image from 'next/image';
import style from './dropdown.module.scss';

const DropdownMenu = React.forwardRef(
  ({ options, isSelected, handleSelect, menuStyle, dropUp }, ref) => (
    <ul
      ref={ref}
      className={`${style.dropdownMenu} ${style.open} ${dropUp ? style.dropUp : ''}`}
      style={menuStyle}
    >
      {options.map(option => (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <li
          key={option.id}
          className={`${style.dropdownItem} ${isSelected(option.id) ? style.selected : ''}`}
          onClick={e => {
            e.stopPropagation();
            handleSelect(option);
          }}
        >
          {option.name}
        </li>
      ))}
    </ul>
  ),
);

DropdownMenu.displayName = 'DropdownMenu';

DropdownMenu.propTypes = {
  options: PropTypes.array.isRequired,
  isSelected: PropTypes.func.isRequired,
  handleSelect: PropTypes.func.isRequired,
  menuStyle: PropTypes.object.isRequired,
  dropUp: PropTypes.bool.isRequired,
};

const PLDropDown = ({
  options,
  defaultOption,
  placeHolder,
  onOptionSelected,
  previousSelectedOptions = [],
  multipleSelection = false,
  message,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const [selectedOptions, setSelectedOptions] = useState(() =>
    previousSelectedOptions.filter(prev => options.some(opt => opt.id === prev.id)),
  );

  useEffect(() => {
    setSelectedOptions(
      previousSelectedOptions.filter(prev => options.some(opt => opt.id === prev.id)),
    );
  }, [previousSelectedOptions, options]);

  const formattedSelectedOptions = useMemo(() => {
    return selectedOptions.map(option => option.name).join(', ');
  }, [selectedOptions]);

  const calculatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const menuHeight = Math.min(options.length * 41, 220);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const shouldDropUp = spaceBelow < menuHeight + 5 && spaceAbove > spaceBelow;

    setDropUp(shouldDropUp);
    setMenuStyle({
      position: 'fixed',
      width: rect.width,
      left: rect.left,
      ...(shouldDropUp
        ? { bottom: window.innerHeight - rect.top + 5, top: 'auto' }
        : { top: rect.bottom + 5, bottom: 'auto' }),
    });
  }, [options.length]);

  const toggleDropdown = useCallback(
    e => {
      e.stopPropagation();
      if (!isOpen) {
        calculatePosition();
      }
      setIsOpen(prev => !prev);
    },
    [isOpen, calculatePosition],
  );

  const handleSelect = useCallback(
    newOption => {
      if (!multipleSelection) {
        setSelectedOptions([newOption]);
        onOptionSelected([newOption]);
      } else {
        setSelectedOptions(prevSelectedOptions => {
          const exists = prevSelectedOptions.some(item => item.id === newOption.id);
          const updatedOptions = exists
            ? prevSelectedOptions.filter(option => option.id !== newOption.id)
            : [...prevSelectedOptions, newOption];
          onOptionSelected(updatedOptions);
          return updatedOptions;
        });
      }
      setIsOpen(false);
    },
    [onOptionSelected, multipleSelection],
  );

  const isSelected = useCallback(
    id => selectedOptions.some(item => item.id === id),
    [selectedOptions],
  );

  // Close dropdown when clicking outside or scrolling outside the menu
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClose = () => setIsOpen(false);
    const handleScroll = e => {
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setIsOpen(false);
    };
    window.addEventListener('click', handleClose);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      window.removeEventListener('click', handleClose);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  return (
    <div className={style.container} ref={containerRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className={`${style.dropdownSelector} ${isOpen ? style.open : ''}`}
      >
        <span>
          {formattedSelectedOptions || defaultOption || placeHolder || 'Select an option'}
        </span>
        <div className={style.counter}>
          <Image
            className={style.inputIcon}
            src="/images/arrow_down.svg"
            alt="icon"
            width={30}
            height={30}
          />
        </div>
      </button>

      {message && <span className={style.warningMessage}>{message}</span>}

      {isOpen &&
        ReactDOM.createPortal(
          <DropdownMenu
            ref={menuRef}
            options={options}
            isSelected={isSelected}
            handleSelect={handleSelect}
            menuStyle={menuStyle}
            dropUp={dropUp}
          />,
          document.body,
        )}
    </div>
  );
};

PLDropDown.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    }),
  ).isRequired,
  defaultOption: PropTypes.string,
  placeHolder: PropTypes.string,
  onOptionSelected: PropTypes.func.isRequired,
  previousSelectedOptions: PropTypes.array,
  multipleSelection: PropTypes.bool,
  message: PropTypes.string,
};

export default React.memo(PLDropDown);
